import React, { FC, memo, useCallback, useEffect, ReactNode, useContext, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FormikContext } from 'formik';
import noop from 'lodash/noop';

import { EditNextItem, EditPreviousItem, ToggleDialog } from '../IFormModal';

import { modalFormActionsStyles } from './modalFormActionsStyles';

const DialogActionsWrapperMemo = ({ children }: { children: ReactNode }) => {
    const classes = modalFormActionsStyles();

    return (
        <DialogActions className={classes.borderShadow + ' ' + classes.padding2}>{children}</DialogActions>
    );
};

export const DialogActionsWrapper = memo(DialogActionsWrapperMemo);

interface Props {
    toggleDialog: ToggleDialog;
    isEdit?: Boolean;
    editNextItem?: EditNextItem;
    editPreviousItem?: EditPreviousItem;
    onSubmit?: (e?: React.FormEvent<HTMLFormElement> | undefined) => void | undefined;
    disableButtons?: boolean;
    setSubmitButtonClicked?: React.Dispatch<React.SetStateAction<'save' | 'save-exit'>> | null;
    setSaveEditButtonClicked?: React.Dispatch<React.SetStateAction<'save' | 'save-edit'>> | null;
    /** Wizard step navigation (opt-in; used by wizardMode forms). */
    showStepBack?: boolean;
    showStepNext?: boolean;
    onStepBack?: () => void;
    onStepNext?: () => void;
    /** When true, Back is visible but disabled (first step). */
    stepBackDisabled?: boolean;
    /** When true, Next is visible but disabled (required fields incomplete). */
    stepNextDisabled?: boolean;
    stepNextTooltip?: string;
    /** Opt-in: disable the save actions when the form has no unsaved changes
     *  (Formik `dirty` is false). Off by default so existing forms that may save
     *  a pristine form are unaffected. */
    requireDirty?: boolean;
    /**
     * Opt-in: what Cancel does instead of closing outright. TabsDialogForm passes
     * its unsaved-changes guard here, so Cancel warns on the same terms as the
     * title bar's X rather than discarding the user's edits without a word.
     * Defaults to `toggleDialog`, which is what every other caller keeps.
     */
    onCancel?: () => void;
}

const ModalFormActions: FC<Props> = ({
    isEdit = false,
    // NO `= noop` default. The Previous/Next pair is gated on
    // `editNextItem && editPreviousItem`, and a noop default is truthy — so a caller
    // that doesn't support record-stepping (the CRM Passengers page, for one) still
    // got both buttons, and clicking them did nothing. Undefined means "this caller
    // has no next record", which is the only honest way to render that.
    editPreviousItem,
    editNextItem,
    toggleDialog,
    onSubmit,
    disableButtons,
    setSubmitButtonClicked,
    setSaveEditButtonClicked,
    requireDirty = false,
    showStepBack = false,
    showStepNext = false,
    onStepBack = noop,
    onStepNext = noop,
    stepBackDisabled = false,
    stepNextDisabled = false,
    stepNextTooltip = '',
    onCancel
}: Props) => {
    const classes = modalFormActionsStyles();
    const formProps = useContext(FormikContext);

    // Track whether form was saved — only used for Cancel→Close text change
    const [hasSaved, setHasSaved] = useState(false);
    const lastSubmitCountRef = useRef(formProps?.submitCount || 0);

    useEffect(() => {
        const currentCount = formProps?.submitCount || 0;
        if (currentCount > lastSubmitCountRef.current) {
            lastSubmitCountRef.current = currentCount;
            setHasSaved(true);
        }
        if (currentCount < lastSubmitCountRef.current) {
            lastSubmitCountRef.current = currentCount;
        }
    }, [formProps?.submitCount]);

    const handleSubmitForm = useCallback(() => {
        if (onSubmit) {
            onSubmit();
            if (formProps?.setSubmitting) {
                formProps.setSubmitting(false);
            }
        } else {
            if (formProps?.handleSubmit) {
                formProps.handleSubmit();
            }
        }
    }, [formProps, onSubmit]);

    const disabled = disableButtons || formProps?.isSubmitting;
    // With `requireDirty`, disable the save actions when the form has no unsaved
    // changes. `dirty` is true whenever a field differs from the last-saved
    // baseline (setFieldValue edits, incl. the Accounting tab and Change Rate,
    // all flip it); a save rebaselines it back to false. Opt-in so the many other
    // forms sharing this component keep their existing save-always behavior.
    const saveDisabled = disabled || (requireDirty && formProps ? !formProps.dirty : false);

    return (
        <DialogActionsWrapper>
            <Grid container justifyContent="space-between">
                <Grid className={classes.flexGridGap1}>
                    {showStepBack && (
                        <Button
                            onClick={onStepBack}
                            variant="text"
                            startIcon={<ChevronLeftIcon />}
                            disabled={disabled || stepBackDisabled}
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 400,
                                // Chevron sized to sit with the label (matched weight),
                                // ~1px above the text, with a consistent 6px gap.
                                '& .MuiSvgIcon-root': { fontSize: '1.0625rem' },
                                '& .MuiButton-startIcon': {
                                    marginRight: '6px',
                                    marginLeft: 0,
                                    transition: 'transform 0.2s ease'
                                },
                                '&:hover .MuiButton-startIcon': { transform: 'translateX(-3px)' }
                            }}
                        >
                            Back
                        </Button>
                    )}
                    {showStepNext && (
                        <Tooltip title={stepNextDisabled ? stepNextTooltip : ''}>
                            {/* span wrapper lets the tooltip show while the button is disabled */}
                            <span>
                                <Button
                                    onClick={onStepNext}
                                    // Minimal text button like Back. Colour is the cue:
                                    // grey while the step is incomplete, primary (blue)
                                    // once it's ready to proceed — no heavy solid fill.
                                    // Force the colours via sx so a theme-level Button
                                    // override can't render the enabled state near-black.
                                    color="primary"
                                    variant="text"
                                    endIcon={<ChevronRightIcon />}
                                    disabled={disabled || stepNextDisabled}
                                    sx={{
                                        color: stepNextDisabled ? 'text.secondary' : 'primary.main',
                                        fontWeight: stepNextDisabled ? 400 : 600,
                                        // Same chevron size + gap as Back for symmetry.
                                        '& .MuiSvgIcon-root': { fontSize: '1.0625rem' },
                                        '& .MuiButton-endIcon': {
                                            marginLeft: '6px',
                                            marginRight: 0,
                                            transition: 'transform 0.2s ease'
                                        },
                                        '&:hover .MuiButton-endIcon': { transform: 'translateX(3px)' }
                                    }}
                                >
                                    Next
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {isEdit && editNextItem && editPreviousItem && (
                        <>
                            <Button
                                onClick={editPreviousItem}
                                color="primary"
                                disabled={formProps?.isSubmitting}
                                variant="outlined"
                            >
                                Previous
                            </Button>
                            <Button
                                onClick={editNextItem}
                                variant="outlined"
                                color="primary"
                                disabled={formProps?.isSubmitting}
                            >
                                Next
                            </Button>
                        </>
                    )}
                </Grid>
                <Grid className={classes.flexGridGap1}>
                    {/* Cancel/Close is NOT gated on `disableButtons`. That flag
                        means "this form cannot be saved" — no permission, or a
                        read-only imported record — and a form you may not save is
                        exactly the one you most need to be able to close. Only an
                        in-flight submit should hold the dialog open. */}
                    <Button
                        onClick={onCancel || toggleDialog}
                        disabled={formProps?.isSubmitting}
                        variant="outlined"
                    >
                        {hasSaved ? 'Close' : 'Cancel'}
                    </Button>
                    {!!setSubmitButtonClicked && (
                        <Button
                            onClick={
                                setSubmitButtonClicked
                                    ? () => {
                                          setSubmitButtonClicked('save');
                                          setTimeout(() => handleSubmitForm(), 250);
                                      }
                                    : handleSubmitForm
                            }
                            variant="outlined"
                            color="primary"
                            disabled={saveDisabled}
                        >
                            {isEdit ? 'Update' : 'Save'}
                        </Button>
                    )}
                    {!!setSaveEditButtonClicked && (
                        <Button
                            onClick={
                                setSaveEditButtonClicked
                                    ? () => {
                                          setSaveEditButtonClicked('save-edit');
                                          setTimeout(() => handleSubmitForm(), 250);
                                      }
                                    : handleSubmitForm
                            }
                            variant="outlined"
                            color="primary"
                            disabled={saveDisabled}
                        >
                            {isEdit ? 'Update' : 'Edit'}
                        </Button>
                    )}
                    <Button
                        onClick={
                            setSubmitButtonClicked
                                ? () => {
                                      setSubmitButtonClicked('save-exit');
                                      setTimeout(() => handleSubmitForm(), 250);
                                  }
                                : handleSubmitForm
                        }
                        variant="contained"
                        color="primary"
                        disabled={saveDisabled}
                    >
                        {`${isEdit ? 'Update' : 'Save'} & Exit`}
                    </Button>
                </Grid>
            </Grid>
        </DialogActionsWrapper>
    );
};

export default memo(ModalFormActions);
